import { Box, Stack, Typography } from '@mui/material';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import { forwardRef, useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import { Fade, FieldWrapper, MorePill, TextAreaBase } from './expandable-text-area.elements';
import { ExpandableTextAreaProps } from './expandable-text-area.types';

const DEFAULT_COLLAPSED_HEIGHT = 164;

/**
 * V2 multiline free-text field that collapses to a base height and expands to
 * fit its full content. See the redesign mockup (Preferences → Agent
 * instructions).
 */
export const ExpandableTextArea = forwardRef<HTMLTextAreaElement, ExpandableTextAreaProps>(
  (
    {
      value,
      defaultValue,
      onChange,
      onBlur,
      title,
      action,
      collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
      defaultExpanded = false,
      placeholder,
      disabled = false,
      spellCheck = false,
      id,
      name,
      showMoreLabel = 'Show more',
      showLessLabel = 'Show less',
      inputRef,
      sx = {},
    },
    forwardedRef,
  ) => {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [internalValue, setInternalValue] = useState(defaultValue ?? '');
    const [height, setHeight] = useState(collapsedHeight);
    const [hasOverflow, setHasOverflow] = useState(false);
    const localRef = useRef<HTMLTextAreaElement | null>(null);
    // Reset to `auto` to read the natural scrollHeight, then pin the height in
    // the same tick. `width` is a dep so wrapping reflows re-measure; the effect
    // only writes height, so the observed width can't feed back into a loop.
    const { ref: sizeRef, width } = useResizeObserver<HTMLTextAreaElement>();
    const combinedRef = useForkRef(inputRef, forwardedRef, localRef, sizeRef);

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    useLayoutEffect(() => {
      const el = localRef.current;
      if (!el) return;
      el.style.height = 'auto';
      const style = getComputedStyle(el);
      const borders = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
      const full = el.scrollHeight + borders;
      const next = expanded ? full : Math.min(full, collapsedHeight);
      el.style.height = `${next}px`;
      setHasOverflow(full > collapsedHeight + 1);
      setHeight(next);
    }, [currentValue, collapsedHeight, expanded, width]);

    const handleChange = useCallback<NonNullable<ExpandableTextAreaProps['onChange']>>(
      (event) => {
        if (!isControlled) {
          setInternalValue(event.target.value);
        }
        onChange?.(event);
      },
      [isControlled, onChange],
    );

    const toggle = useCallback(() => setExpanded((prev) => !prev), []);

    return (
      <Stack spacing={1} sx={sx}>
        {title || action ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
            {title ? (
              <Typography
                component="label"
                htmlFor={fieldId}
                variant="subtitle2"
                fontWeight={700}
                color="custom.OV.sys.onBackground"
              >
                {title}
              </Typography>
            ) : (
              <span />
            )}
            {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
          </Stack>
        ) : null}

        <FieldWrapper>
          <TextAreaBase
            id={fieldId}
            name={name}
            expanded={expanded}
            reservePillSpace={expanded || hasOverflow}
            ref={combinedRef}
            value={currentValue}
            placeholder={placeholder}
            disabled={disabled}
            spellCheck={spellCheck}
            onChange={handleChange}
            onBlur={onBlur}
            style={{ height }}
          />
          {!expanded && hasOverflow ? <Fade /> : null}
          {expanded || hasOverflow ? (
            <MorePill type="button" onClick={toggle}>
              {expanded ? showLessLabel : showMoreLabel}
            </MorePill>
          ) : null}
        </FieldWrapper>
      </Stack>
    );
  },
);

ExpandableTextArea.displayName = 'ExpandableTextArea';
